import React from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { BarChart3, TrendingUp, DollarSign, AlertTriangle } from 'lucide-react';

const Analytics = () => {
  const { products = [], orders = [] } = useAdminAuth();

  // 1. Uğurlu (Təsdiqlənmiş və Çatdırılmış) Sifarişlər (Case-insensitive)
  const successfulOrders = orders.filter(o => {
    const st = (o.status || '').toUpperCase();
    return st === 'CONFIRMED' || st === 'DELIVERED' || st === 'TƏSDİQLİ' || st === 'ÇATDIRILDI';
  });

  // 2. Ümumi Gəlir (totalAmount və ya total fall-back ilə)
  const totalRevenue = successfulOrders.reduce((sum, o) => sum + Number(o.totalAmount ?? o.total ?? 0), 0);

  // 3. Orta Sifariş Məbləği (AOV)
  const averageOrderValue = successfulOrders.length > 0 ? totalRevenue / successfulOrders.length : 0;

  // 4. Anbarın Ümumi Maliyyə Dəyəri (Stok * Qiymət)
  const totalInventoryValue = products.reduce((sum, p) => {
    const price = Number(p.price || 0);
    const stock = Number(p.stockQuantity ?? p.stock ?? 0);
    return sum + (price * stock);
  }, 0);

  // 5. Kateqoriyalar üzrə Stok Payı
  const categoryStats = products.reduce((acc, p) => {
    const cat = p.categoryName || p.category;
    if (cat) {
      const stock = Number(p.stockQuantity ?? p.stock ?? 0);
      acc[cat] = (acc[cat] || 0) + stock;
    }
    return acc;
  }, {});

  // 6. Kritik Stokda Olanlar (Stok sayısı 5-dən az və ya bərabər olanlar)
  const lowStockProducts = products.filter(p => Number(p.stockQuantity ?? p.stock ?? 0) <= 5);

  return (
    <div className="space-y-6 sm:space-y-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-serif text-gray-900">Analitika və Hesabatlar</h1>
        <p className="text-sm text-gray-500 mt-1">L'Aliel butikinin satış, anbar və maliyyə hesabatlarının detallı analizi.</p>
      </div>

      {/* Maliyyə Göstəriciləri Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Ümumi Net Satış</span>
            <DollarSign className="w-5 h-5 text-emerald-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{totalRevenue.toFixed(2)} ₼</p>
          <p className="text-xs text-emerald-600 mt-1 font-medium">Təsdiqlənmiş və Çatdırılmış</p>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Orta Sifariş Məbləği</span>
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{averageOrderValue.toFixed(2)} ₼</p>
          <p className="text-xs text-gray-500 mt-1">Sifariş başına düşən orta gəlir</p>
        </div>

        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Anbarın Ümumi Dəyəri</span>
            <BarChart3 className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 break-words">{totalInventoryValue.toFixed(2)} ₼</p>
          <p className="text-xs text-gray-500 mt-1">Mövcud stokun satış qiyməti ilə cəmi</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Kateqoriya Payları */}
        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-4">Kateqoriyalar üzrə Stok Dağılımı</h3>
          <div className="space-y-4">
            {Object.keys(categoryStats).length === 0 ? (
              <p className="text-sm text-gray-400">Məlumat tapılmadı.</p>
            ) : (
              Object.entries(categoryStats).map(([category, count]) => (
                <div key={category}>
                  <div className="flex justify-between text-sm font-medium mb-1">
                    <span className="text-gray-700">{category}</span>
                    <span className="text-gray-900">{count} ədəd</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-black h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min((count / 100) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Kritik Stok Xəbərdarlığı */}
        <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" /> Kritik Stok Xəbərdarlığı
          </h3>
          <div className="divide-y divide-gray-50 max-h-60 overflow-y-auto">
            {lowStockProducts.length === 0 ? (
              <p className="text-sm text-emerald-600 font-medium py-4">Bütün məhsulların stoku kifayət qədərdir! 🎉</p>
            ) : (
              lowStockProducts.map(product => {
                const stock = Number(product.stockQuantity ?? product.stock ?? 0);
                return (
                  <div key={product.id} className="py-3 flex items-center justify-between first:pt-0 last:pb-0">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{product.name}</p>
                      <p className="text-xs text-gray-500">{product.categoryName || product.category || 'Geyim'}</p>
                    </div>
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${
                      stock === 0 ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-amber-50 text-amber-700 border border-amber-100'
                    }`}>
                      {stock === 0 ? 'Tükənib' : `${stock} ədəd qaldı`}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;